import React from 'react';
import { Box, Typography, Divider } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { formatEventTimestamp } from '../../../utils/dateUtils';
import StatusChip from '../../../components/StatusChip';
import { getEventStatusColor } from '../../../utils/statusChipUtil';

/**
 * EventRow component renders a single event row in the events list
 * @param {Object} event - Event object with id, status, event_type, event_timestamp
 * @param {boolean} isLast - Whether this is the last item in the list
 * @param {string} selectedEventId - Currently selected event ID for highlighting
 * @param {string} statusChipWidth - Width for status chip container to ensure consistent spacing
 */
const EventRow = ({ event, isLast, selectedEventId, statusChipWidth }) => {
  const navigate = useNavigate();
  const isSelected = selectedEventId === event.id;

  const handleClick = () => {
    navigate(`/events/${event.id}`);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  };

  // Get status for status chip
  const eventStatus = event.status;
  const statusColor = getEventStatusColor(eventStatus);

  return (
    <>
      <Box
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 1.5,
          py: 1.25,
          cursor: 'pointer',
          borderRadius: '6px',
          border: isSelected ? '2px solid' : '2px solid transparent',
          borderColor: isSelected ? 'primary.main' : 'transparent',
          backgroundColor: 'transparent',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            backgroundColor: isSelected ? 'transparent' : 'action.hover',
          },
          '&:focus': {
            borderColor: 'primary.main',
            outline: 'none',
          },
        }}
        tabIndex={0}
      >
        {/* Left section: Status chip and Event name */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
          {eventStatus && (
            <Box sx={{ width: statusChipWidth, display: 'flex' }}>
              <StatusChip color={statusColor} label={eventStatus} size="small" />
            </Box>
          )}
          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 500, fontSize: '0.875rem' }}>
            {event.event_type}
          </Typography>
        </Box>

        {/* Right section: Time */}
        <Box sx={{ ml: 1.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
            {formatEventTimestamp(event.event_timestamp)}
          </Typography>
        </Box>
      </Box>
      {!isLast && <Divider />}
    </>
  );
};

export default EventRow;
